<template>
  <TagCreateDialog
    v-model:visibility="dialogCreateVisible"
    @saved="(newTag) => handleNewTagCreated(newTag)"
    @closed="newTagPerson = null" />

  <div class="header">
    <h3>{{ i18n.t('informations') }}</h3>
    <div
      v-require-permission="CompanyAccessRole.ADMIN"
      style="display: flex; gap: 4px">
      <SmButton
        v-if="!editMode"
        size="small"
        outline
        @click="editMode = !editMode">
        <SquarePen class="mr-2 text-contrast" :size="18" />
        {{ i18n.t('general.edit') }}
      </SmButton>
      <SmButton
        v-if="editMode"
        type="danger"
        size="small"
        @click="
          () => {
            editMode = !editMode
          }
        ">
        <X :size="16" class="text-contarst" />
        <span style="padding-left: 0.3rem">{{ i18n.t('general.cancel') }}</span>
      </SmButton>
      <SmButton
        v-if="editMode"
        type="success"
        size="small"
        @click="updatePersonData">
        <CircleCheck :size="16" class="text-white" />
        <span style="padding-left: 0.3rem"> {{ i18n.t('general.save') }}</span>
      </SmButton>
    </div>
  </div>

  <div class="person-information">
    <div class="person-item">
      <span class="information-heading">{{ i18n.t('firstName') }}</span>
      <span v-if="!editMode && person.first_name == ''" class="no-data">{{
        i18n.t('general.noData')
      }}</span>
      <span v-else-if="!editMode">{{ person.first_name }}</span>
      <SmInput v-else v-model="localPerson.first_name" size="small" outline />
    </div>

    <div class="person-item">
      <span class="information-heading">{{ i18n.t('lastName') }}</span>
      <span v-if="!editMode && person.last_name == ''" class="no-data">{{
        i18n.t('general.noData')
      }}</span>
      <span v-else-if="!editMode">{{ person.last_name }}</span>
      <SmInput v-else v-model="localPerson.last_name" size="small" outline />
    </div>

    <div class="person-item">
      <span class="information-heading">{{ i18n.t('displayName') }}</span>
      <span v-if="!editMode && person.name == ''" class="no-data">{{
        i18n.t('general.noData')
      }}</span>
      <span v-else-if="!editMode">{{ person.name }}</span>
      <SmInput v-else v-model="localPerson.name" size="small" outline />
    </div>

    <div></div>
  </div>

  <!-- Contact information -->
  <h3 style="margin-top: 2rem; margin-bottom: 1rem">
    {{ i18n.t('contactData') }}
  </h3>
  <div class="person-information">
    <div class="person-item">
      <span class="information-heading">{{ i18n.t('phone') }}</span>
      <span v-if="!editMode && person.phone == ''" class="no-data">{{
        i18n.t('general.noData')
      }}</span>
      <span v-else-if="!editMode">{{ person.phone }}</span>
      <SmInput v-else v-model="localPerson.phone" size="small" outline />
    </div>

    <div class="person-item">
      <span class="information-heading">{{ i18n.t('email') }}</span>
      <span v-if="!editMode && !person.email" class="no-data">{{
        i18n.t('general.noData')
      }}</span>
      <span v-else-if="!editMode">{{ person.email }}</span>
      <SmInput
        v-else
        v-model="localPerson.email"
        size="small"
        outline
        disabled />
    </div>
  </div>

  <!-- Company internal -->
  <h3 style="margin-top: 2rem; margin-bottom: 1rem">
    {{ i18n.t('companyInternal') }}
  </h3>
  <div class="person-information">
    <div class="person-item">
      <span class="information-heading">{{ i18n.t('employeeId') }}</span>
      <span v-if="!editMode && person.employee_id == ''" class="no-data">{{
        i18n.t('general.noData')
      }}</span>
      <span v-else-if="!editMode">{{ person.employee_id }}</span>
      <SmInput v-else v-model="localPerson.employee_id" size="small" outline />
    </div>

    <!-- Tags -->
    <div class="person-item">
      <span class="information-heading">{{ i18n.t('tags') }}</span>
      <!-- <div> -->
      <div class="tag-wrapper">
        <template v-for="tag in person.tags">
          <TagComponent
            v-if="
              availableTags.find((availableTag) => availableTag._id === tag)
            "
            :key="tag"
            :tag="
              availableTags.find((availableTag) => availableTag._id === tag)!
            " />
        </template>

        <TagSelect
          show-create-tag
          :available-tags="availableTags"
          :selected="person.tags"
          @delete-tag="handleTagChange('delete', $event)"
          @add-tag="handleTagChange('add', $event)"
          @create-tag="openCreateTagDialog(person)">
          <Plus :size="16" class="tag-add-icon" />
        </TagSelect>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { useI18n } from 'vue-i18n'
  import { CircleCheck, Plus, X } from 'lucide-vue-next'
  import {
    Account,
    AccountUpdateIn,
    CompanyAccessRole,
    Tag,
    TagsService,
  } from '@/client'
  import { SquarePen } from 'lucide-vue-next'
  import { PersonStore } from '@/stores/person/person.store'

  function handleTagChange(method: 'add' | 'delete', tagId: string) {
    const tags = [...props.person.tags]

    switch (method) {
      case 'add':
        tags.push(tagId)
        break
      case 'delete':
        tags.splice(tags.indexOf(tagId), 1)
        break
    }

    const updates = ref<AccountUpdateIn>({
      _id: props.person._id,
      tags: tags,
    })

    PersonStore.updatePerson(props.person._id, updates.value)
  }

  const props = defineProps({
    person: {
      type: Object as () => Account,
      required: true,
    },
  })
  const emit = defineEmits(['update:person'])

  const i18n = useI18n()

  const editMode = ref(false)

  const localPerson = computed({
    // making sure, that the v-models for the inputs have a value
    get: () => {
      return {
        ...props.person,
        last_name: props.person.last_name || '',
        first_name: props.person.first_name || '',
        name: props.person.name || '',
        phone: props.person.phone || '',
        employee_id: props.person.employee_id || '',
      }
    },
    set: (value) => emit('update:person', value),
  })

  async function updatePersonData() {
    editMode.value = !editMode.value
    if (!localPerson.value) return

    const updates = ref<AccountUpdateIn>({
      email: localPerson.value.email,
      name: localPerson.value.name,
      phone: localPerson.value.phone,
      employee_id: localPerson.value.employee_id,
      first_name: localPerson.value.first_name,
      last_name: localPerson.value.last_name,
    })

    PersonStore.updatePerson(props.person._id, updates.value)
  }

  const dialogCreateVisible = ref(false)
  const availableTags = ref<Tag[]>([])
  const newTagPerson = ref<Account | null>(null)

  function loadTags() {
    TagsService.getTagsApiV1ManagementTagsGet({}).then((tags) => {
      availableTags.value = tags
    })
  }

  function openCreateTagDialog(person: Account) {
    dialogCreateVisible.value = true
    newTagPerson.value = person
  }

  function handleNewTagCreated(tag: Tag) {
    loadTags()
    handleTagChange('add', tag._id)
  }

  onMounted(async () => {
    availableTags.value = await TagsService.getTagsApiV1ManagementTagsGet({})
  })
</script>

<style scoped>
  .header {
    padding-top: 2px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .person-information {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    width: 99%;
  }

  .person-item {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .information-heading {
    font-weight: 300;
    color: theme('colors.gray[400]');
  }
  .tag-wrapper {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 5px;
    padding: 0.5rem 0 0.5rem 0;
    align-items: center;
    width: 100%;
  }
  .tag-wrapper:hover .tag-add-icon {
    opacity: 1;
  }
</style>
